import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    //this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // ===== Grab the variant-picker for THIS product form =====
    const vp = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!vp) return;

    const sizeTableEl = vp.querySelector('[data-size-qty-table]');
    const addToCartTextError = this.refs.addToCartTextError;

    // ✅ Remove error when user enters any valid quantity
    if (sizeTableEl && addToCartTextError) {
      sizeTableEl.addEventListener('input', (e) => {
        if (!e.target.matches('[data-qty-input]')) return;

        const totalQty = Array.from(sizeTableEl.querySelectorAll('[data-qty-input]'))
          .reduce((sum, input) => sum + (Number(input.value) || 0), 0);

        if (totalQty > 0) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.textContent = '';
        }
      });
    }

    // ===== Imprinting UI behavior (supports imprint UI OUTSIDE <variant-picker>) =====
    (() => {
      // 1) Get the correct variant-picker for THIS product
      const vp = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
      if (!vp) return;

      // size table stays inside variant-picker
      const sizeTable = vp.querySelector('[data-size-qty-table]');
      if (!sizeTable) return;

      // 2) Find imprint UI OUTSIDE variant-picker, but in the same product section
      //    (best: wrap imprint UI in a container with same section id or inside same .shopify-section)
      const section = this.closest('.shopify-section') || document;
      const imprintWrap = section.querySelector('[data-imprint-wrap]');
      const imprintSelect = section.querySelector('[data-imprint-select]');
      const posWrap = section.querySelector('[data-imprint-positions]');
      const detailsWrap = section.querySelector('[data-imprint-details]');
      const addOnJsonEl = section.querySelector('[data-imprint-product]');
      if (!imprintWrap || !imprintSelect || !posWrap || !detailsWrap) return;

      imprintWrap.hidden = false;

      // Build dynamic location/type buttons (from data attributes on detailsWrap)
      const locList = JSON.parse(detailsWrap.dataset.locations || '[]');
      const typeList = JSON.parse(detailsWrap.dataset.types || '[]');

      const buildButtons = (container, items, attr) => {
        container.innerHTML = '';
        items.forEach((label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'imprint-btn';
          b.textContent = label;
          b.dataset[attr] = label;
          container.appendChild(b);
        });
      };

      [1, 2, 3].forEach((i) => {
        const locGroup = detailsWrap.querySelector(`[data-location-group="${i}"]`);
        const typeGroup = detailsWrap.querySelector(`[data-type-group="${i}"]`);
        if (locGroup) buildButtons(locGroup, locList, 'loc');
        if (typeGroup) buildButtons(typeGroup, typeList, 'type');
      });

      const getMaxSizeQty = () => {
        return Array.from(sizeTable.querySelectorAll('[data-qty-input]'))
          .reduce((m, input) => Math.max(m, Number(input.value) || 0), 0);
      };

      const resetImprintUI = () => {
        imprintSelect.value = '';
        imprintSelect.disabled = true;
        posWrap.hidden = true;
        detailsWrap.hidden = true;

        posWrap.querySelectorAll('.imprint-btn.is-active').forEach((b) => b.classList.remove('is-active'));
        detailsWrap.querySelectorAll('.imprint-btn.is-active').forEach((b) => b.classList.remove('is-active'));
        detailsWrap.querySelectorAll('[data-imprint-position]').forEach((box) => (box.hidden = true));
      };

      const getTotalSizeQty = () => {
        return Array.from(sizeTable.querySelectorAll('[data-qty-input]'))
          .reduce((sum, input) => sum + (Number(input.value) || 0), 0);
      };


      const enableIfEligible = () => {
        const eligible = getTotalSizeQty() >= 10; // ✅ SUM of all sizes
        imprintSelect.disabled = !eligible;

        if (!eligible) resetImprintUI();
      };


      // Yes/No dropdown
      imprintSelect.addEventListener('change', () => {
        if (imprintSelect.value === 'yes') {
          posWrap.hidden = false;
        } else {
          posWrap.hidden = true;
          detailsWrap.hidden = true;
          resetImprintUI();
          // keep dropdown value as selected no/please select
          imprintSelect.disabled = false;
          imprintSelect.value = imprintSelect.value; 
        }
      });

      // Position buttons (One/Two/Three)
      posWrap.addEventListener('click', (e) => {
        const btn = e.target.closest('.imprint-btn[data-pos]');
        if (!btn) return;

        posWrap.querySelectorAll('.imprint-btn').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        const posCount = Number(btn.dataset.pos || 0);
        detailsWrap.hidden = false;

        detailsWrap.querySelectorAll('[data-imprint-position]').forEach((box) => {
          const n = Number(box.getAttribute('data-imprint-position'));
          box.hidden = !(n <= posCount);
        });
      });

      // Location/type active state toggles
      detailsWrap.addEventListener('click', (e) => {
        const b = e.target.closest('.imprint-btn');
        if (!b) return;

        const group = b.parentElement;
        if (!group) return;

        group.querySelectorAll('.imprint-btn').forEach((x) => x.classList.remove('is-active'));
        b.classList.add('is-active');
      });

      // Keep dropdown enabled/disabled live based on qty inputs
      sizeTable.addEventListener('input', (e) => {
        if (!e.target.matches('[data-qty-input]')) return;
        enableIfEligible();
      });

      // Initial
      enableIfEligible();

      // Store add-on variants JSON for submit handler (same as before)
      this.__imprintAddonVariants = addOnJsonEl ? JSON.parse(addOnJsonEl.textContent || '[]') : [];


      // =====================
      // ATC DISABLE UNTIL VALID (size qty + imprint selections)
      // Put this INSIDE connectedCallback(), after you have vp/sizeTable/imprintSelect/posWrap/detailsWrap
      // =====================

      const updateAtcState = () => {
        const atc = this.refs.addToCartButtonContainer; // AddToCartComponent
        if (!atc?.refs?.addToCartButton) return;

        // If theme already disabled due to unavailable variant, don't force enable
        const variantIdInput = this.querySelector('input[name="id"]');
        const variantSelected = !!(variantIdInput && variantIdInput.value);

        // Default assume valid
        let isValid = true;

        // 1) If size table exists: require at least one qty > 0
        if (sizeTable) {
          const totalQty = Array.from(sizeTable.querySelectorAll('[data-qty-input]'))
            .reduce((sum, input) => sum + (Number(input.value) || 0), 0);

          if (totalQty <= 0) isValid = false;
        }

        // 2) If imprinting "yes": require all imprint choices (comments optional)
        if (vp && imprintSelect && imprintSelect.value === 'yes') {
          // must have min 10 in ANY size
          const maxQty = sizeTable
            ? Array.from(sizeTable.querySelectorAll('[data-qty-input]'))
                .reduce((m, input) => Math.max(m, Number(input.value) || 0), 0)
            : 0;

          const totalQty = getTotalSizeQty();
          if (totalQty < 10) isValid = false;

          // must select position count
          const activePosBtn = posWrap?.querySelector('.imprint-btn.is-active[data-pos]');
          const posCount = Number(activePosBtn?.dataset.pos || 0);
          if (!posCount) isValid = false;

          // for each visible position box: require Location + Type selected
          if (detailsWrap && posCount) {
            for (let i = 1; i <= posCount; i++) {
              const box = detailsWrap.querySelector(`[data-imprint-position="${i}"]`);
              if (!box || box.hidden) continue;

              const locActive = box.querySelector('[data-location-group] .imprint-btn.is-active');
              const typeActive = box.querySelector('[data-type-group] .imprint-btn.is-active');

              if (!locActive || !typeActive) {
                isValid = false;
                break;
              }

              // OPTIONAL: If you want file required when Type = "image", uncomment this:
              // const selectedType = (typeActive.dataset.type || '').toLowerCase();
              // if (selectedType === 'image') {
              //   const fileInput = box.querySelector(`input[type="file"][data-file="${i}"]`);
              //   if (!fileInput || !fileInput.files || !fileInput.files[0]) {
              //     isValid = false;
              //     break;
              //   }
              // }
            }
          }
        }

        // Also require a variant is selected (safety)
        if (!variantSelected) isValid = false;

        // Apply state
        if (isValid) atc.enable();
        else atc.disable();
      };

      // Run once on load
      updateAtcState();

      // Keep it updated on qty typing
      sizeTable?.addEventListener('input', (e) => {
        if (!e.target.matches('[data-qty-input]')) return;
        updateAtcState();
      });

      // imprint dropdown change
      imprintSelect?.addEventListener('change', updateAtcState);

      // position buttons click
      posWrap?.addEventListener('click', (e) => {
        if (!e.target.closest('.imprint-btn')) return;
        updateAtcState();
      });

      // location/type button clicks
      detailsWrap?.addEventListener('click', (e) => {
        if (!e.target.closest('.imprint-btn')) return;
        updateAtcState();
      });

      // When theme updates variant HTML (ajax), call again shortly
      setTimeout(updateAtcState, 0);

    })();

  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #setAtcLoading(isLoading) {
    const atcContainer = this.refs.addToCartButtonContainer;
    const btn = atcContainer?.refs?.addToCartButton;
    if (!btn) return;

    
    // add class on the custom element wrapper if possible, else on button
    const wrap = atcContainer instanceof HTMLElement ? atcContainer : btn;
    wrap.classList.toggle('is-loading', isLoading);
    
    console.log("checkss");
    console.log(wrap);

    btn.disabled = isLoading;
    btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }



  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();


    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );
    const sizeTable = variantPicker?.querySelector('[data-size-qty-table]');

if (sizeTable) {

    
  // ✅ stop any other submit logic from running
  event.stopPropagation();
  event.stopImmediatePropagation();

  const form = this.querySelector('form');
  if (!form) throw new Error('Product form element missing');

  // ----- helpers (inline so it works even if your earlier methods are wrong) -----
  const getSelectedOptionValues = () => {
  const out = {};
  if (!variantPicker) return out;

  // radios (Sense button style)
  variantPicker.querySelectorAll('input[type="radio"]:checked').forEach((inp) => {
    const optionName = (inp.getAttribute('data-option-name') || inp.name.split('-')[0] || '').trim();
    if (optionName) out[optionName] = inp.value;
  });

  // selects (Sense dropdown style)
  variantPicker.querySelectorAll('select[name^="options["]').forEach((sel) => {
    const m = sel.name.match(/^options\[(.+?)\]$/);
    if (m && m[1]) out[m[1]] = sel.value;
  });

  return out;
};


  const findVariant = (variants, productOptionsOrder, selectedMap) => {
    const want = productOptionsOrder.map((optName) => selectedMap[optName]);
    return variants.find((v) => {
      if (!Array.isArray(v.options)) return false;
      for (let i = 0; i < want.length; i++) {
        if (v.options[i] !== want[i]) return false;
      }
      return true;
    });
  };

  // ----- build items[] from qty inputs -----
  const variants = JSON.parse(sizeTable.dataset.variants || '[]');
  const productOptionsOrder = JSON.parse(sizeTable.dataset.productOptions || '[]');
  const sizeOptionName = sizeTable.dataset.sizeOptionName;

  const selected = getSelectedOptionValues(); // includes color/material etc (NOT size)

  const items = [];
  sizeTable.querySelectorAll('.size-qty-row').forEach((row) => {
    const sizeVal = row.getAttribute('data-size-value');
    const qtyInput = row.querySelector('[data-qty-input]');
    const qty = qtyInput ? Number(qtyInput.value || 0) : 0;

    if (!sizeVal || qty <= 0) return;

    const selectedWithSize = { ...selected, [sizeOptionName]: sizeVal };
    const variant = findVariant(variants, productOptionsOrder, selectedWithSize);

    if (variant && variant.available !== false) {
      items.push({ id: variant.id, quantity: qty });
    }
  });

  // ✅ If user entered qty but no matching variants found, block submit
  if (!items.length) {
    const { addToCartTextError } = this.refs;
    const msg = 'Please enter quantity for at least one available size.';
    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) textNode.textContent = msg;
      else addToCartTextError.appendChild(document.createTextNode(msg));
    }
    return;
  }


  // ===== Add imprint add-on if eligible/selected =====
    const vp = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const section = this.closest('.shopify-section') || document;
    const imprintSelect = section.querySelector('[data-imprint-select]');
    const posWrap = section.querySelector('[data-imprint-positions]');
    const detailsWrap = section.querySelector('[data-imprint-details]');
    const addonVariants = this.__imprintAddonVariants || [];


    const maxQty = (() => {
      const st = vp?.querySelector('[data-size-qty-table]');
      if (!st) return 0;
      return Array.from(st.querySelectorAll('[data-qty-input]'))
        .reduce((m, input) => Math.max(m, Number(input.value) || 0), 0);
    })();

    const getTotalSizeQty = () => {
      return Array.from(sizeTable.querySelectorAll('[data-qty-input]'))
        .reduce((sum, input) => sum + (Number(input.value) || 0), 0);
    };


    let imprintAddOn = null;

    if (vp && imprintSelect && imprintSelect.value === 'yes') {

      const totalQty = getTotalSizeQty();
      if (totalQty < 10) {
        // force reset if somehow selected before
        imprintSelect.value = '';
      } else {
        const activePosBtn = posWrap?.querySelector('.imprint-btn.is-active[data-pos]');
        const posCount = Number(activePosBtn?.dataset.pos || 0);

        if (posCount > 0) {
          const addonVariants = this.__imprintAddonVariants || [];

            // Choose the exact title we expect
            const posLabel =
              posCount === 1 ? 'one position' :
              posCount === 2 ? 'two position' :
              'three position';

            const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

            const addonVariant = (addonVariants || []).find(v => norm(v.title).includes(posLabel));

            // OPTIONAL fallback (if store sometimes changes $ formatting)
            const addonVariantFallback =
              addonVariant ||
              addonVariants.find((v) => norm(v.title).includes(norm(posCount === 1 ? 'one position' : posCount === 2 ? 'two position' : 'three position')));

            const finalAddonVariant = addonVariantFallback;

            const totalSizeQty = (() => {
              const st = vp?.querySelector('[data-size-qty-table]');
              if (!st) return 0;

              return Array.from(st.querySelectorAll('[data-qty-input]'))
                .reduce((sum, input) => sum + (Number(input.value) || 0), 0);
            })();


            if (!finalAddonVariant) {
              console.warn('Imprint add-on variant not found. Needed:', titleNeed, 'Available:', addonVariants.map(v => v.title));
            } else {
              imprintAddOn = {
                id: finalAddonVariant.id,
                quantity: totalSizeQty,
                properties: {}
              };
            }
          
          console.log(addonVariant);
          if (addonVariant) {
          

            // Collect selections for each visible position
            const toWord = (n) => (n === 1 ? 'First' : n === 2 ? 'Second' : 'Third');

            for (let i = 1; i <= posCount; i++) {
              const box = detailsWrap?.querySelector(`[data-imprint-position="${i}"]`);
              if (!box) continue;

              const locBtn = box.querySelector('[data-location-group] .imprint-btn.is-active');
              const typeBtn = box.querySelector('[data-type-group] .imprint-btn.is-active');
              const fileInput = box.querySelector(`[data-file="${i}"]`);

              imprintAddOn.properties[`${toWord(i)} Position - Location`] = locBtn?.dataset.loc || '';
              imprintAddOn.properties[`${toWord(i)} Position - Type`] = typeBtn?.dataset.type || '';

              // file (optional; you can make required when type is Image if you want)
              if (fileInput && fileInput.files && fileInput.files[0]) {
                imprintAddOn.properties[`${toWord(i)} Position - File`] = fileInput.files[0];
              }
            }

            const comments = document.querySelector('[data-imprint-comments]')?.value || '';
            if (comments) imprintAddOn.properties['Instructions'] = comments;
          }
        }
      }
    }


  // ----- submit multi add -----
  const formData = new FormData(form);

  // remove single add fields so Shopify doesn't add first variant
  formData.delete('id');
  formData.delete('quantity');

  let idx = 0;

  items.forEach((it) => {
    formData.append(`items[${idx}][id]`, String(it.id));
    formData.append(`items[${idx}][quantity]`, String(it.quantity));
    idx++;
  });

  if (imprintAddOn) {
    formData.append(`items[${idx}][id]`, String(imprintAddOn.id));
    formData.append(`items[${idx}][quantity]`, String(imprintAddOn.quantity));

    // properties (including file objects)
    Object.entries(imprintAddOn.properties || {}).forEach(([k, v]) => {
      // If value is a File, append as file
      if (v instanceof File) formData.append(`items[${idx}][properties][${k}]`, v, v.name);
      else formData.append(`items[${idx}][properties][${k}]`, String(v || ''));
    });

    idx++;
  }


  // keep theme sections logic
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  });
  formData.append('sections', sectionIds.join(','));

  const fetchCfg = fetchConfig('javascript', { body: formData });

  this.#setAtcLoading(true);

  fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: { ...fetchCfg.headers, Accept: 'text/html' },
  })
    .then((r) => r.json())
    .then((response) => {
      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
        );
        return;
      }

      this.refs.addToCartButtonContainer?.animateAddToCart();

      this.#fetchAndUpdateCartQuantity();

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-form-component',
          itemCount: items.reduce((a, b) => a + b.quantity, 0),
          productId: this.dataset.productId,
          sections: response.sections,
        })
      );
    })
    .catch(console.error)
    .finally(() => {
          this.#setAtcLoading(false);

      cartPerformance.measureFromEvent('add:user-action', event);
    });

  return; // ✅ VERY IMPORTANT: stop normal single-add
}




    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
